/**
 * Message Bubble
 *
 * @deprecated Task 4053b — the divergent `Message` pipeline
 * (MessageThread / MessageBubble / MessageInput + the session-store message
 * plumbing) is retained ONLY for `features/log-feature/LogFeatureModal`.
 * All other surfaces render the canonical `AgentSessionTranscript`
 * (`features/sessions/components/`).  Do not add new consumers — a
 * follow-up task migrates the LogFeatureModal and deletes these files.
 *
 * Renders a single message in the thread.
 * - User messages: right-aligned, muted-accent card with subtle accent
 *   border and shadow (theming inherits from --accent-primary so the
 *   bubble reflects the platform's theming rather than burning a saturated
 *   primary). Asymmetric corners give it a chat-tail.
 * - Agent messages: left-aligned prose, no bubble background, no avatar —
 *   alignment alone signals role, matching modern professional chat UIs
 *   (Claude, ChatGPT, Linear, Granola). Long structured responses read
 *   like normal markdown.
 * - Markdown rendering for agent messages (bold, lists, code, links).
 * - Inline: tool calls, tool results, memory ops, artifacts.
 * - Streaming indicator: animated cursor block at end while isStreaming.
 *
 * Ordering invariant — for agent messages we iterate `message.parts` in
 * the order they arrived from the stream so a tool call that preceded
 * the agent's text renders ABOVE the text, never bucketed below it.
 * Falls back to the legacy `content` + `toolCalls`/`memoryOps`/
 * `artifacts` buckets for messages without `parts` (e.g. programmatic
 * inserts and old tests).
 */

import { useMemo, type CSSProperties } from 'react'
import { ChatMarkdown } from '@/shared/components/ChatMarkdown'
import { looksLikeMarkdown } from '@/shared/lib/looksLikeMarkdown'
import type { Message, MessagePart } from '../types'
import { ToolCallCard } from './ToolCallCard'
import { MemoryOpCard } from './MemoryOpCard'
import { ArtifactInline } from './ArtifactInline'

export function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user'

  // Markdown-structured user prompts (the big ticket/context blobs handed to
  // agents) render through ChatMarkdown so headings/bold/lists/code render
  // structurally instead of as a raw-syntax wall of text. Plain user text
  // keeps its pre-wrap rendering (Markdown collapses single newlines).
  // Memoised so the prompt scan doesn't re-run on every parent re-render.
  const isUserMarkdown = useMemo(
    () => isUser && looksLikeMarkdown(message.content),
    [isUser, message.content],
  )
  // A "significant" user message (long or Markdown-structured) earns extra
  // width so it reads like a document rather than a cramped chat bubble.
  const isSignificantUser =
    isUser && (isUserMarkdown || message.content.length > 320)

  return (
    <div
      style={{
        ...styles.container,
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
      data-testid={`message-bubble-${message.id}`}
      data-role={message.role}
    >
      <div
        style={{
          ...styles.bubble,
          ...(isUser ? styles.userBubble : styles.agentBubble),
          ...(isSignificantUser ? styles.userBubbleWide : {}),
        }}
      >
        {isUser ? (
          isUserMarkdown ? (
            <div style={styles.userContent}>
              <ChatMarkdown text={message.content} />
              {message.isStreaming && (
                <span style={styles.cursor} className="animate-pulse" />
              )}
            </div>
          ) : (
            <div style={styles.userContentPlain}>
              {message.content}
              {message.isStreaming && (
                <span style={styles.cursor} className="animate-pulse" />
              )}
            </div>
          )
        ) : (
          <AgentBody message={message} />
        )}

        {message.error && (
          <div style={styles.errorBlock}>
            Error: {message.error}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Agent body ────────────────────────────────────────────────────────────────

function AgentBody({ message }: { message: Message }) {
  // Preferred path: render parts in arrival order so a tool call placed
  // before the agent's text stays above the text in the bubble.
  if (message.parts && message.parts.length > 0) {
    return (
      <div style={styles.agentContent}>
        {message.parts.map((part, idx) => (
          <PartRenderer
            key={idx}
            part={part}
            // Cursor only on the final text part while streaming
            showCursor={
              !!message.isStreaming &&
              idx === message.parts!.length - 1 &&
              part.kind === 'text'
            }
          />
        ))}
        {/* If the message is streaming but the very last part is non-text,
            still show a trailing cursor so the operator sees the thread
            is "live". */}
        {message.isStreaming &&
          (message.parts[message.parts.length - 1]?.kind !== 'text') && (
            <span style={styles.cursor} className="animate-pulse" />
          )}
      </div>
    )
  }

  // Legacy fallback — content blob first, then any bucketed extras.
  return (
    <div style={styles.agentContent}>
      <ChatMarkdown text={message.content} />
      {message.isStreaming && (
        <span style={styles.cursor} className="animate-pulse" />
      )}
      {message.toolCalls && message.toolCalls.length > 0 && (
        <div style={styles.attachmentsAgent}>
          {message.toolCalls.map((toolCall) => (
            <ToolCallCard
              key={toolCall.id}
              toolCall={toolCall}
              result={message.toolResults?.find((r) => r.toolCallId === toolCall.id)}
            />
          ))}
        </div>
      )}
      {message.memoryOps && message.memoryOps.length > 0 && (
        <div style={styles.attachmentsAgent}>
          {message.memoryOps.map((memOp) => (
            <MemoryOpCard key={memOp.id} memOp={memOp} />
          ))}
        </div>
      )}
      {message.artifacts && message.artifacts.length > 0 && (
        <div style={styles.attachmentsAgent}>
          {message.artifacts.map((artifact) => (
            <ArtifactInline key={artifact.id} artifact={artifact} />
          ))}
        </div>
      )}
    </div>
  )
}

function PartRenderer({
  part,
  showCursor,
}: {
  part: MessagePart
  showCursor: boolean
}) {
  switch (part.kind) {
    case 'text':
      // Skip rendering an empty text segment outright — it would still
      // produce a paragraph of vertical space inside the bubble.
      if (!part.text && !showCursor) return null
      return (
        <div style={styles.textPart}>
          {part.text && <ChatMarkdown text={part.text} />}
          {showCursor && (
            <span style={styles.cursor} className="animate-pulse" />
          )}
        </div>
      )
    case 'tool_call':
      return (
        <div style={styles.inlinePart}>
          <ToolCallCard toolCall={part.toolCall} result={part.result} />
        </div>
      )
    case 'memory_op':
      return (
        <div style={styles.inlinePart}>
          <MemoryOpCard memOp={part.memoryOp} />
        </div>
      )
    case 'artifact':
      return (
        <div style={styles.inlinePart}>
          <ArtifactInline artifact={part.artifact} />
        </div>
      )
  }
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
  },
  bubble: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    fontSize: '14px',
    minWidth: 0,
  },
  userBubble: {
    maxWidth: 'min(75%, 520px)',
    background: 'color-mix(in srgb, var(--accent-primary) 16%, transparent)',
    color: 'var(--text-primary)',
    border: '1px solid color-mix(in srgb, var(--accent-primary) 30%, transparent)',
    borderRadius: 'var(--radius-lg) var(--radius-lg) var(--radius-sm) var(--radius-lg)',
    padding: '10px 14px',
    lineHeight: 1.55,
    boxShadow: '0 2px 8px color-mix(in srgb, #000 25%, transparent)',
  },
  agentBubble: {
    maxWidth: 'min(94%, 640px)',
    background: 'transparent',
    color: 'var(--text-primary)',
    padding: '2px 0 0 0',
    lineHeight: 1.6,
  },
  // Wrapper for Markdown-rendered user content — no pre-wrap, the Markdown
  // renderer manages its own block spacing.
  userContent: {
    wordBreak: 'break-word',
  },
  // Wrapper for plain (non-Markdown) user content — preserve newlines.
  userContentPlain: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  // Width override applied to significant (long / Markdown) user messages so
  // they read like a document instead of a cramped chat bubble.
  userBubbleWide: {
    maxWidth: 'min(94%, 720px)',
  },
  agentContent: {
    display: 'flex',
    flexDirection: 'column',
    // Gap between text parts and tool-call cards within a single agent
    // message. Larger than the inline default so the boundaries between
    // prose and structured cards are obvious.
    gap: '14px',
    wordBreak: 'break-word',
  },
  textPart: {
    // Anchor for inline cursor; markdown sets its own block spacing
    // via the global .markdown / .chat-md rules.
  },
  inlinePart: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  cursor: {
    marginLeft: '2px',
    display: 'inline-block',
    width: '2px',
    height: '1em',
    verticalAlign: 'text-bottom',
    background: 'currentColor',
  },
  errorBlock: {
    borderRadius: 'var(--radius-sm)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    background: 'rgba(239, 68, 68, 0.1)',
    padding: '8px',
    fontSize: '12px',
    color: 'var(--error)',
  },
  attachmentsAgent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
}
